import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import Box from '@material-ui/core/Box'

import { pushApiError } from '../../../../../Reducers/Errors/actions'
import { showReduxModal } from '../../../../../Reducers/UI/actions'

import OnboardingQuizView from '../component/OnboardingQuizView'
import QuizQuestionsForm from '../../../../../Components/QuizQuestionsForm/container/QuizQuestionsForm'

import { apiClient } from '../../../../../Module/API/Client'
import { getQuiz, setQuiz, duplicateQuiz } from '../../../../../Module/graphQL'
import useGraphQLFetch from '../../../../../Utils/Hooks/useGraphQLFetch'
import { useAmbassadorsRequest } from '../../Ambasadors/gql'
import {
  QUIZ_TYPES,
  DELETE_PITCH_FILE_ERROR,
} from '../../../../../shared/constants'
import VideoQuizQuestionsForm from '../../../../../Components/VideoQuizQuestionsForm/container/VideoQuizQuestionsForm'
import VoQuizParts from '../../../../../Components/VideoQuizQuestionsForm/blocks/VoQuizPartsView/container/VoQuizParts'
import { DropzoneWithUrlV2 } from '../../../../../Components/Form/DropzoneWithUrlV2'

const OnboardingQuiz = ({ cityId, isShowedQuiz }) => {
  const [progress, setProgress] = useState(null)
  const [pitchUrl, setPitchUrl] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [quizQuestions, setQuizQuestions] = useState([])
  const [uploading, setUploading] = useState(false)
  const [selectedQuizType, setSelectedQuizType] = useState('onboarding')
  const [partOneQuizQuestions, setPartOneQuizQuestions] = useState([])
  const [numberOfQuestions, setNumberOfQuestions] = useState(null)
  const dispatch = useDispatch()
  const { getAmbassadors } = useAmbassadorsRequest(cityId)

  const fetchAmbassadorsInTest = async () => {
    const { totalCount } = await getAmbassadors(
      {
        statuses: ['QUIZ_IN_PROGRESS'],
      },
      {
        isFresh: true,
      },
    )
    return totalCount
  }

  useEffect(() => {
    if (isShowedQuiz) {
      setSelectedQuizType('onboarding')
    } else {
      setSelectedQuizType('videoOnboarding')
    }
  }, [isShowedQuiz])

  const quizOptions = isShowedQuiz
    ? [
        { label: 'Onboarding Quiz', value: 'onboarding' },
        { label: 'Video Onboarding Quiz', value: 'videoOnboarding' },
      ]
    : [{ label: 'Video Onboarding Quiz', value: 'videoOnboarding' }]

  const fetchArgs = {
    type: QUIZ_TYPES.ONBOARDING_PITCH,
    cityId,
  }
  // Promofy video onboarding is always SINGLE (one video), so the quiz has
  // exactly one part.
  const fetchArgsPart1 = {
    type: QUIZ_TYPES.VIDEO_ONBOARDING_PART_ONE,
    cityId,
  }

  const { data, isLoaded, refetch } = useGraphQLFetch({
    fetchFn: getQuiz,
    defaultFetchArgs: fetchArgs,
    fetchId: 'getQuiz',
    mapResponse: data => data.quiz,
  })

  const {
    data: partOneData,
    isLoaded: partOneLoaded,
    refetch: partOneRefetch,
  } = useGraphQLFetch({
    fetchFn: getQuiz,
    defaultFetchArgs: fetchArgsPart1,
    fetchId: 'getQuizOne',
    mapResponse: data => data.quiz,
  })

  const refetchQuiz = async () => {
    refetch(fetchArgs)
  }
  const refetchPartOne = async () => {
    partOneRefetch(fetchArgsPart1)
  }

  const voQuizTypesAndDataMap = [
    {
      type: QUIZ_TYPES.VIDEO_ONBOARDING_PART_ONE,
      data: partOneQuizQuestions,
      refetch: refetchPartOne,
      numberOfQuestions: partOneData ? partOneData.amountOfQuestions : null,
    },
  ]

  useEffect(() => {
    if (partOneLoaded) {
      const questions = partOneData.quizQuestions || []
      setNumberOfQuestions(partOneData.amountOfQuestions)
      setPartOneQuizQuestions(questions)
    }
  }, [partOneLoaded])

  useEffect(() => {
    if (isLoaded) {
      const url = data.url
      const questions = data.quizQuestions || []
      setQuizQuestions(questions)
      if (url) {
        setPitchUrl(url)
      }
    }
  }, [isLoaded])

  const duplicateQuizToAllCities = async () => {
    if (selectedQuizType == 'onboarding') {
      dispatch(
        showReduxModal({
          title: 'Confirmation needed',
          onConfirm: async () => {
            setUploading(true)
            const response = await duplicateQuiz({
              type: QUIZ_TYPES.ONBOARDING_PITCH,
              cityId,
              url: pitchUrl,
              quizQuestions,
            })
            setUploading(false)
            const result = await response.json()
            if (result.errors && result.errors[0])
              throw new Error(result.errors[0].message)
          },
          children: (
            <Box
              display="flex"
              flexWrap="wrap"
              justifyContent="center"
              mt={3}
              mb={3}
            >
              Are you sure you want to copy quiz to other cities?
            </Box>
          ),
        }),
      )
    } else {
      dispatch(
        showReduxModal({
          title: 'Confirmation needed',
          onConfirm: async () => {
            try {
              setUploading(true)
              await Promise.all(
                voQuizTypesAndDataMap.map(async quiz => {
                  const response = await duplicateQuiz({
                    type: quiz.type,
                    cityId,
                    quizQuestions: quiz.data,
                    amountOfQuestions: quiz.numberOfQuestions,
                  })
                  const result = await response.json()
                  if (result.errors && result.errors[0])
                    throw new Error(result.errors[0].message)
                }),
              )
              setUploading(false)
              setTimeout(() => {
                dispatch(
                  showReduxModal({
                    title: 'Quiz copied to other cities!',
                    confirmLabel: 'Ok',
                    hideCancel: true,
                  }),
                )
              }, 1000)
            } catch (error) {
              setUploading(false)
            }
          },
          children: (
            <Box
              display="flex"
              flexWrap="wrap"
              justifyContent="center"
              mt={3}
              mb={3}
            >
              Video onboarding quiz will be replaced in the whole country, are
              you sure?
            </Box>
          ),
        }),
      )
    }
  }

  const onChange = async ([pitchFile]) => {
    setProgress(20)
    const pitchUrl = await apiClient.uploadQuizPitchFile(cityId, pitchFile)
    setProgress(75)
    const response = await setQuiz({
      type: QUIZ_TYPES.ONBOARDING_PITCH,
      cityId,
      url: pitchUrl,
    })
    const result = await response.json()
    if (result.errors && result.errors[0])
      throw new Error(result.errors[0].message)
    setProgress(100)
    setPitchUrl(pitchUrl)
    setProgress(null)
  }

  const onDelete = async () => {
    const totalCount = await fetchAmbassadorsInTest()
    if (totalCount) {
      dispatch(pushApiError(DELETE_PITCH_FILE_ERROR))
      return
    }
    setIsDeleting(true)
    const response = await setQuiz({
      type: QUIZ_TYPES.ONBOARDING_PITCH,
      cityId,
      url: null,
    })
    const result = await response.json()
    if (result.errors && result.errors[0])
      throw new Error(result.errors[0].message)
    setPitchUrl(null)
    setIsDeleting(false)
  }

  const allVoPartHasQuestions = partOneQuizQuestions.length > 0

  const saveNumberOfQuestions = async () => {
    await Promise.all(
      voQuizTypesAndDataMap.map(async item => {
        const response = await setQuiz({
          type: item.type,
          cityId,
          amountOfQuestions: numberOfQuestions,
        })
        const result = await response.json()
        if (result.errors && result.errors[0])
          throw new Error(result.errors[0].message)
      }),
    )
    await refetchPartOne()
  }

  const onNumberOfQuestionsSave = async () => {
    if (partOneQuizQuestions.length < numberOfQuestions) {
      return dispatch(
        pushApiError(
          `Please add at least ${numberOfQuestions} questions to your quiz`,
        ),
      )
    }
    dispatch(
      showReduxModal({
        title: 'Confirmation needed',
        onConfirm: saveNumberOfQuestions,
        children: (
          <Box
            display="flex"
            flexWrap="wrap"
            justifyContent="center"
            mt={3}
            mb={3}
          >
            {`Do you want to set ${numberOfQuestions} questions per quiz attempt?`}
          </Box>
        ),
      }),
    )
  }

  return (
    <OnboardingQuizView
      allVoPartHasQuestions={allVoPartHasQuestions}
      selectedQuizType={selectedQuizType}
      setQuizType={setSelectedQuizType}
      quizOptions={quizOptions}
      pitchUrl={pitchUrl}
      questionsLength={quizQuestions.length}
      onDelete={onDelete}
      isDeleting={isDeleting}
      duplicateQuizToAllCities={duplicateQuizToAllCities}
      uploading={uploading}
      fileUploader={
        <DropzoneWithUrlV2
          progress={progress}
          input={{ onChange }}
          meta={{}}
          src={pitchUrl}
        />
      }
    >
      {pitchUrl && selectedQuizType === 'onboarding' && (
        <QuizQuestionsForm
          cityId={cityId}
          fetchQuizQuestions={refetchQuiz}
          quizQuestions={quizQuestions}
        />
      )}
      {selectedQuizType === 'videoOnboarding' && (
        <VoQuizParts
          numberOfQuestions={numberOfQuestions}
          setNumberOfQuestions={setNumberOfQuestions}
          onNumberOfQuestionsSave={onNumberOfQuestionsSave}
        >
          {voQuizTypesAndDataMap.map(part => (
            <VideoQuizQuestionsForm
              cityId={cityId}
              fetchQuizQuestions={part.refetch}
              quizQuestions={part.data}
              videoOnboardingPart={part.type}
              numberOfQuestions={part.numberOfQuestions}
            />
          ))}
        </VoQuizParts>
      )}
    </OnboardingQuizView>
  )
}

export default OnboardingQuiz
